import React, { FC } from 'react';
import { Cascader } from 'antd';

import { FieldProps, Field } from 'formik';
import Spinner from '@atlaskit/spinner';

import { LoadingTexto } from '../Tipografia/Texto';
import { SpinnerContainer } from '../Botao/Botao';
import { iconeSetinhaSelect } from '../Icone/Icones';
import { useCampoFormularioProps } from 'service/restricao/useCampoFormularioProps/useCampoFormularioProps';
import './cascader.scss'


export interface CascaderProps {
  className?: string
  name: string
  placeholder?: string
  isLoading?: boolean
  disabled?: boolean
}

export interface CascaderFieldProps extends CascaderProps {
  floresta: No[]
}

export interface No {  
  id?: number | null,
  nome: string,
  filhos?: No[]
}

export interface CascadeOption<T extends No> {
  entidade: T,
  label: string,
  value: number,
  pai?: CascadeOption<T>,
  children?: CascadeOption<T>[]
}


export const CascaderField: FC<CascaderFieldProps> = (props) => {
  const restricaoProps = useCampoFormularioProps(props.name, undefined);
  const options = buildOptions(props.floresta);

  if (restricaoProps.hidden) 
    return <></>
  
  if (restricaoProps.readOnly) {
    return <Field {...props}>
      {(formikProps: FieldProps) => <ConteudoVisualizacao formikProps={formikProps} {...props} />}
    </Field>
  }

  const CascaderWithOnBlur = Cascader as any

  const iconeCascader = props.isLoading === true
    ? <SpinnerContainer><Spinner size="small" /></SpinnerContainer>
    : iconeSetinhaSelect();

  return (
    <Field {...props}>
      {(formikProps: FieldProps) => {
        const valor = getDefaultValue(options, formikProps.field.value, e => e.value);

        return <CascaderWithOnBlur
          name={props.name}
          suffixIcon={iconeCascader}
          disabled={restricaoProps.disabled === true || formikProps.form.isSubmitting}
          className={`cascader ${props.className ?? ''}`}
          popupClassName={'cascader-popup'}
          showSearch={{ filter }}

          options={options.options}
          value={valor}
          allowClear={restricaoProps.clearable === true}
          placeholder={props.placeholder}
          onBlur={formikProps.field.onBlur}
          
          size="middle"
          onChange={(value: any) => formikProps.form.setFieldValue(formikProps.field.name, value[value.length - 1])}
          changeOnSelect  />
        }
      }
    </Field>
  )
}


interface ConteudoVisualizacaoProps extends CascaderFieldProps {
  formikProps: FieldProps;
}

const ConteudoVisualizacao: FC<ConteudoVisualizacaoProps> = ({formikProps, ...props}) => {
  const restricaoProps = useCampoFormularioProps(props.name, undefined);
  const options = buildOptions(props.floresta);
  const valor = getDefaultValue(options, formikProps.field.value, e => e.label);
  const hasValor = valor.length !== 0;
  const carregando = restricaoProps.loading;

  if (!hasValor && carregando) {
    return <LoadingTexto width={220} />
  }

  return hasValor ? <>{valor.join(" > ")}{carregando && <> <Spinner /></>}</> : <>A definir</>
}


function filter(inputValue: any, path: any) {
  return path.some((option: any) => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
}

type HashCascadeOption<T extends No> = {[key: string]: CascadeOption<T>};

export interface CascadeOptions<T extends No> {
  mapa: HashCascadeOption<T>,
  options: CascadeOption<T>[]
}


export function buildOptions<T extends No>(floresta: T[], pai?: CascadeOption<T>): CascadeOptions<T> {
  const mapa: HashCascadeOption<T> = {}

  return {
    mapa: mapa,
    options: floresta.map(no => buildCascaderOptions<T>(mapa, no))
  }
}

function buildCascaderOptions<T extends No>(mapa: HashCascadeOption<T>, no: T, pai?: CascadeOption<T>): CascadeOption<T> {
  const option: CascadeOption<T> = {
    entidade: no,
    label: no.nome,
    value: no.id ?? 0,
    pai: pai
  };

  mapa[option.value] = option;

  option.children = no.filhos?.map(no => buildCascaderOptions<T>(mapa, no as T, option));

  return option;
}

export function getDefaultValue<T extends No, Q>(opcoes: CascadeOptions<T>, id: number, extrator: (c: CascadeOption<T>) => Q): Q[] {
  const opcao = opcoes.mapa[id];

  const caminho: Q[] = [];
  let no: CascadeOption<T>|undefined = opcao;

  while (no != null) {
    caminho.push(extrator(no))
    no = no.pai
  }

  return caminho.reverse()
}
